/**
 * Droppable gap between dashboard rows (insert a section after a row).
 */

import * as React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { ArrowDown, Undo2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DASHBOARD_DROP_ZONE_LABELS, SOURCE_ROW_PLACEHOLDER_ID } from './dashboard-configs';
import { dropZoneAfterRowId } from './dashboard-utils';

/** Shared dashed drop-target chrome used by the dashboard layout placeholders. */
interface DropZoneContentProps {
  label: string;
  icon: React.ReactNode;
  isOver: boolean;
  className?: string;
  style?: React.CSSProperties;
}

/** Dashed placeholder shown while a dashboard section is dragged over a slot. */
const DropZoneContent: React.FC<DropZoneContentProps> = ({
  label,
  icon,
  isOver,
  className,
  style,
}) => (
  <div
    className={cn(
      'w-full rounded-xl border-2 border-dashed transition-all duration-200 flex items-center justify-center gap-2',
      isOver
        ? 'border-[var(--color-2-nuance-1)] bg-[color-mix(in_srgb,var(--color-2-nuance-1)_12%,transparent)]'
        : 'border-[color-mix(in_srgb,var(--color-2-nuance-1)_40%,transparent)] bg-[color-mix(in_srgb,var(--color-2-nuance-1)_4%,transparent)]',
      className,
    )}
    style={style}
  >
    <span
      className={cn(
        'transition-colors duration-200',
        isOver
          ? 'text-[var(--color-2-nuance-1)]'
          : 'text-[color-mix(in_srgb,var(--color-2-nuance-1)_50%,transparent)]',
      )}
    >
      {icon}
    </span>
    <span
      className={cn(
        'font-medium text-sm transition-colors duration-200',
        isOver
          ? 'text-[var(--color-2-nuance-1)]'
          : 'text-[color-mix(in_srgb,var(--color-2-nuance-1)_50%,transparent)]',
      )}
    >
      {label}
    </span>
  </div>
);

/** Droppable gap after a wrap row (`afterRowIdx === -1` is the top gap). */
export const DropZoneBetweenRows: React.FC<{ afterRowIdx: number; visible: boolean }> = ({
  afterRowIdx,
  visible,
}) => {
  const { setNodeRef, isOver } = useDroppable({ id: dropZoneAfterRowId(afterRowIdx) });

  if (!visible) return null;

  return (
    <div
      ref={setNodeRef}
      className={cn('col-span-12 transition-all duration-200', isOver ? 'h-24' : 'h-16')}
    >
      <DropZoneContent
        label={DASHBOARD_DROP_ZONE_LABELS.dropHere}
        icon={<ArrowDown className="w-4 h-4" />}
        isOver={isOver}
        className="h-full"
      />
    </div>
  );
};

/** Placeholder that keeps the source row height while a lone section is dragged. */
export const SourceRowPlaceholder: React.FC<{ height: number }> = ({ height }) => {
  const { setNodeRef, isOver } = useDroppable({ id: SOURCE_ROW_PLACEHOLDER_ID });

  return (
    <div ref={setNodeRef} className="col-span-12">
      <DropZoneContent
        label={DASHBOARD_DROP_ZONE_LABELS.bringBack}
        icon={<Undo2 className="w-4 h-4" />}
        isOver={isOver}
        style={{ height: `${height}px` }}
      />
    </div>
  );
};

/** Inline slot preview inside a wrap row while inserting a section. */
export const InlineDropPlaceholder: React.FC<{ height: number }> = ({ height }) => (
  <DropZoneContent
    label={DASHBOARD_DROP_ZONE_LABELS.bringBack}
    icon={<Undo2 className="w-4 h-4" />}
    isOver={true}
    style={{ height: `${height}px` }}
  />
);
